import { sleep } from '@remote-voice/utilities';

/**
 * AudioContext Singleton
 * iOS Safariでの自動再生制約問題を解決するため、AudioContextを単一インスタンスで管理
 */
class AudioContextSingleton {
  private static instance: AudioContextSingleton | null = null;
  private audioContext: AudioContext | null = null;
  private isInitializing = false;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  public static getInstance(): AudioContextSingleton {
    if (!AudioContextSingleton.instance) {
      AudioContextSingleton.instance = new AudioContextSingleton();
    }
    return AudioContextSingleton.instance;
  }

  public isContextAvailable(): boolean {
    return this.audioContext !== null && this.audioContext.state === 'running';
  }

  public getContextState(): AudioContextState | null {
    return this.audioContext?.state || null;
  }

  /**
   * AudioContextを強制的にリセット（テスト用途など）
   */
  public async reset(): Promise<void> {
    if (this.audioContext) {
      try {
        if (this.audioContext.state !== 'closed') {
          await this.audioContext.close();
        }
      } catch (error) {
        console.warn('Error closing AudioContext during reset:', error);
      }
      this.audioContext = null;
    }
    this.isInitializing = false;
  }

  public async getAudioContext(): Promise<AudioContext> {
    console.debug('[TTS-stream] playChunk', {
      audioContextState: this.audioContext?.state,
      audioContextType: (navigator as any)?.audioSession?.type,
    });
    // 既存のAudioContextが利用可能な場合は再開を試行
    if (Howler.ctx && Howler.ctx.state === 'suspended') {
      try {
        await Howler.ctx.resume();
      } catch (error) {
        console.warn('Failed to resume existing AudioContext:', error);
        // 再開に失敗した場合は新しいAudioContextを作成
        this.audioContext = null;
      }
    }

    if (Howler.ctx && Howler.ctx.state === 'running') {
      this.audioContext = Howler.ctx;
      return this.audioContext;
    }

    // 初期化中の場合は待機
    if (this.isInitializing) {
      return new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          if (!this.isInitializing) {
            clearInterval(checkInterval);
            if (this.audioContext && this.audioContext.state === 'running') {
              resolve(this.audioContext);
            } else {
              reject(new Error('AudioContext initialization failed'));
            }
          }
        }, 10);
      });
    }

    // 新しいAudioContextを作成
    // if state is interrupted, create new audio context
    return this.createNewAudioContext();
  }

  private async createNewAudioContext(): Promise<AudioContext> {
    this.isInitializing = true;

    try {
      if ((Howler.ctx.state as string) === 'interrupted') {
        Howler.unload();
        await sleep(100);

        // AudioContext再生成後に現状エラーが出るので、初回のみ実行
        if (Howler.ctx == null) {
          new Howl({
            src: ['/sample_audio.mp3'],
            onunlock: () => console.log('tts audio unlocked'),
          });
        }
      }

      if (Howler.ctx.state === 'suspended') {
        await Howler.ctx.resume();
      }

      if (Howler.ctx.state !== 'running') {
        throw new Error(
          `AudioContext state is ${Howler.ctx.state}, expected running`
        );
      }
      this.audioContext = Howler.ctx;
      return this.audioContext;
    } catch (error) {
      console.error('Failed to create AudioContext:', error);
      this.audioContext = null;
      throw error;
    } finally {
      this.isInitializing = false;
    }
  }
}

export default AudioContextSingleton;
